/* TITAN WALLS — Template-Kern-Runtime (Gate 3)
   Ein Reveal-Eigentümer für alle Nicht-Homepage-Seiten.
   Misst die untransformierte Layoutposition, latcht den höchsten
   Fortschritt, respektiert Reduced Motion und den Shopify-Editor.
   Materialphasen werden aus demselben Seitenvertrag gelesen wie die
   sichtbaren Flächen, damit Header und Inhalt auch bei verschachtelten
   Hell-/Dunkelwechseln miteinander kommunizieren.
   Keine Dauerschleife und kein Scroll-Hijacking im Editor. */
(() => {
  'use strict';

  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const latched = new WeakMap();
  let intros = [];
  let media = [];
  let sections = [];
  let phaseSurfaces = [];
  let phase = '';
  let queued = false;
  let frame = 0;

  const PHASE_SURFACE_SELECTOR = [
    '.tw-page__section',
    '[data-tw-phase]',
    '.tw-material-light',
    '.tw-service-faq-v2__archive',
    '.tw-house-v2__manifesto',
    '.tw-institution-record-v2__document',
    '.tw-buying-guide-v2__scope',
    '.tw-buying-guide-v2__document',
    '.tw-generic-page-v2__paper'
  ].join(',');

  const isDesignMode = () => document.documentElement.classList.contains('shopify-design-mode');
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const smoothstep = (value) => value * value * (3 - 2 * value);
  const viewportHeight = () => Math.max(1, window.visualViewport?.height || window.innerHeight || 1);

  const translatedY = (node) => {
    const transform = getComputedStyle(node).transform;
    if (!transform || transform === 'none') return 0;

    const values = transform
      .slice(transform.indexOf('(') + 1, -1)
      .split(',')
      .map((value) => Number.parseFloat(value.trim()));

    return transform.startsWith('matrix3d') ? (values[13] || 0) : (values[5] || 0);
  };

  const progress = (node, startRatio, endRatio) => {
    const top = node.getBoundingClientRect().top - translatedY(node);
    const height = viewportHeight();
    const start = height * startRatio;
    const end = height * endRatio;
    return smoothstep(clamp((start - top) / Math.max(1, start - end)));
  };

  const write = (node, property, value, doneClass) => {
    const previous = latched.get(node) || 0;
    const stable = Math.max(previous, value);
    latched.set(node, stable);
    node.style.setProperty(property, stable.toFixed(4));
    if (stable >= .999) node.classList.add(doneClass);
  };

  const explicitPhase = (surface) => surface.dataset.twPhase?.trim().toLowerCase() || '';

  const resolveSurfacePhase = (surface) => {
    const declared = explicitPhase(surface);
    if (declared === 'dark' || declared === 'graphite') return 'dark';
    if (declared === 'light' || declared === 'titan' || declared === 'steel') return 'light';

    if (
      surface.classList.contains('tw-material-light') ||
      surface.classList.contains('tw-service-faq-v2__archive') ||
      surface.classList.contains('tw-house-v2__manifesto') ||
      surface.classList.contains('tw-institution-record-v2__document') ||
      surface.classList.contains('tw-buying-guide-v2__scope') ||
      surface.classList.contains('tw-buying-guide-v2__document') ||
      surface.classList.contains('tw-generic-page-v2__paper')
    ) return 'light';

    if (surface.classList.contains('is-dark') || surface.classList.contains('is-graphite')) return 'dark';
    return 'light';
  };

  const updatePhase = () => {
    if (!phaseSurfaces.length) return;

    const probe = Math.min(viewportHeight() * .09, 96);
    let active = null;

    /* Surfaces are in DOM order. A nested material surface therefore replaces
       its containing root while the probe is physically inside it. When that
       nested surface ends, the containing page section naturally owns the
       phase again. */
    for (const surface of phaseSurfaces) {
      const rect = surface.getBoundingClientRect();
      if (rect.top <= probe && rect.bottom > probe) active = surface;
    }

    if (!active) active = phaseSurfaces[0];
    const next = resolveSurfacePhase(active);

    if (next !== phase) {
      phase = next;
      document.body.dataset.twlhPhase = next;
    }
  };

  const update = () => {
    queued = false;
    frame = 0;

    const staticState = reduced.matches || isDesignMode();
    for (const node of intros) {
      write(node, '--intro-progress', staticState ? 1 : progress(node, .78, .42), 'is-intro-complete');
    }
    for (const node of media) {
      write(node, '--media-progress', staticState ? 1 : progress(node, .84, .52), 'is-media-complete');
    }

    updatePhase();
  };

  const schedule = () => {
    if (queued) return;
    queued = true;
    frame = requestAnimationFrame(update);
  };

  const collect = () => {
    intros = [...document.querySelectorAll('.tw-page__section [data-section-intro]')];
    media = [...document.querySelectorAll('.tw-page__section [data-reveal-media]')];
    sections = [...document.querySelectorAll('.tw-page__section')];
    phaseSurfaces = [...document.querySelectorAll(PHASE_SURFACE_SELECTOR)];
    schedule();
  };

  addEventListener('scroll', schedule, { passive: true });
  addEventListener('resize', schedule, { passive: true });
  window.visualViewport?.addEventListener('resize', schedule, { passive: true });

  if (typeof reduced.addEventListener === 'function') {
    reduced.addEventListener('change', schedule);
  } else {
    reduced.addListener?.(schedule);
  }

  document.fonts?.ready.then(schedule);
  document.addEventListener('visibilitychange', schedule);
  document.addEventListener('shopify:section:load', collect);
  document.addEventListener('shopify:section:unload', collect);
  document.addEventListener('shopify:section:reorder', collect);
  document.addEventListener('shopify:block:select', schedule);

  addEventListener('pagehide', () => {
    if (frame) cancelAnimationFrame(frame);
    queued = false;
    frame = 0;
  }, { once: true });

  collect();
})();
